import React, { useState, useEffect } from 'react';
import CombinedEditableSection from '../../../../../../components/CombinedEditableSection.comp';
import { parseInlineMarkdown } from '../../../../../utils/LearnTab/slideTextRenderer';
import { getSlideId } from '../../../../../utils/LearnTab/preview.utils';
import { tUi } from '../../../../../../../../utils/dq_lc';
import {
  SlideTitle,
} from '../../../../../styles/LearnTab/slideBase.styles';
import {
  ActivityColumnContainer,
  ActivityHeader,
  ActivityPill,
  PollContainer,
  PollOption,
  PollOptionBadge,
  PollOptionText,
  SubmitButton,
} from '../../../../../styles/LearnTab/activity.styles';
import { SlideTitleHeader } from '../common/SlideTitleHeader.comp';
import { renderTextWithId } from '../common/EditableSlideField.comp';

export const PollQuizSlide = ({
  slide,
  highlightWord,
  isEditMode = false,
  slideEdits = {},
  showEnglishContent = false,
  onSaveEdit,
  onResetEdit,
  onCancelEdit,
  uiLocale = 'en-US',
  onNextSlide,
  onPollSubmit,
  pollResponses = {},
}) => {
  const slideId = getSlideId(slide) || slide?.slide_id || slide?.SlideID;
  const recorded = slideId ? pollResponses[slideId] : null;

  const [selectedPollOption, setSelectedPollOption] = useState(
    recorded ? recorded.selectedOption : null
  );
  const [isPollSubmitted, setIsPollSubmitted] = useState(Boolean(recorded));

  useEffect(() => {
    const curr = slideId ? pollResponses[slideId] : null;
    setSelectedPollOption(curr ? curr.selectedOption : null);
    setIsPollSubmitted(Boolean(curr));
  }, [slideId, pollResponses]);

  const handlePollOptionClick = (index) => {
    setSelectedPollOption(index);
    setIsPollSubmitted(false);
  };

  const origin = slide?._pbdq_origin || slide || {};
  const rawOptions = slide?.Options || origin.options || slide?.options || [];
  const pollQuestion = origin.question_text || slide?.SlideContent || slide?.SlideTitle || origin.poll_title || origin.title || '';
  const selectedOptObj = (typeof selectedPollOption === 'number' && rawOptions[selectedPollOption]) ? rawOptions[selectedPollOption] : null;
  const selectedFeedback = typeof selectedOptObj === 'object' && selectedOptObj ? selectedOptObj.feedback_if_chosen : null;
  const isCorrect = Boolean(selectedOptObj && (selectedOptObj.is_correct === true || selectedOptObj.is_correct === 'true'));
  const pollOptionLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

  const handlePollSubmit = () => {
    if (selectedPollOption !== null) {
      setIsPollSubmitted(true);
      if (onPollSubmit) {
        onPollSubmit({
          slideId,
          selectedOption: selectedPollOption,
          feedback: isCorrect ? null : selectedFeedback,
          isCorrect,
        });
      }
      if (onNextSlide) {
        onNextSlide();
      }
    }
  };

  const textRendererOptions = { slideEdits, showEnglishContent, highlightWord };

  return (
    <ActivityColumnContainer style={{ maxWidth: '1500px', gap: '20px' }}>
      <ActivityHeader style={{ width: '100%' }}>
        <ActivityPill>{tUi('slide.activity.pollTime', uiLocale)}</ActivityPill>
        {isEditMode ? (
          <CombinedEditableSection
            slide={slide}
            isEditMode={isEditMode}
            slideEdits={slideEdits}
            showEnglishContent={showEnglishContent}
            onSave={onSaveEdit}
            onReset={onResetEdit}
            onCancel={onCancelEdit}
            TitleComponent={SlideTitle}
            titleExtraProps={{ style: { marginBottom: '24px', paddingBottom: '0px' } }}
          />
        ) : (
          <>
            {pollQuestion ? (
              <SlideTitle style={{ marginBottom: '24px', paddingBottom: '0px' }}>
                {highlightWord ? highlightWord('title', pollQuestion) : pollQuestion}
              </SlideTitle>
            ) : (
              <SlideTitleHeader
                slide={slide}
                slideEdits={slideEdits}
                showEnglishContent={showEnglishContent}
                highlightWord={highlightWord}
                style={{ marginBottom: '24px', paddingBottom: '0px' }}
              />
            )}
          </>
        )}
      </ActivityHeader>
      <PollContainer>
        {Array.isArray(rawOptions) && rawOptions.map((option, idx) => {
          const isObj = typeof option === 'object' && option !== null;
          const optionLabel = isObj ? (option.emoji ? `${option.emoji} ${option.label}` : option.label) : option;
          const isSelected = selectedPollOption === idx;
          return (
            <PollOption
              key={idx}
              selected={isSelected}
              onClick={() => handlePollOptionClick(idx)}
            >
              <PollOptionBadge selected={isSelected}>
                {pollOptionLetters[idx] || idx + 1}
              </PollOptionBadge>
              {renderTextWithId(`poll_option_${idx}`, optionLabel, PollOptionText, {}, textRendererOptions)}
            </PollOption>
          );
        })}
      </PollContainer>
      <SubmitButton
        onClick={handlePollSubmit}
        disabled={selectedPollOption === null}
        style={{
          opacity: selectedPollOption === null ? 0.6 : 1,
          cursor: selectedPollOption === null ? 'not-allowed' : 'pointer',
        }}
      >
        {tUi('slide.activity.submit', uiLocale)}
      </SubmitButton>
    </ActivityColumnContainer>
  );
};

export default PollQuizSlide;
